import { useEffect, useState, type FormEvent } from "react";
import { AI_JOB_IDS, AI_JOBS, AI_MODELS, defaultModel, type AiJobId, type AiModelId } from "@metrickle/schema/ai-models";
import { AI_MODELS_KEY, switchAiModel, useAiModels, type AiModelsAdmin } from "../../lib/admin";
import { useApiMutation } from "../../lib/api";
import {
  Ago,
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  CardSection,
  ErrorState,
  Field,
  Modal,
  None,
  PageHeader,
  RadioCards,
  Skeleton,
  Table,
  TableEmptyRow,
  TableScroll,
  Textarea,
  notifyError,
  toaster,
} from "../../components/ui";
import { usePageTitle } from "./components";

const TRUST_PAGE = "https://metrickle.com/trust/ai";
const usd = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
const price = (m: AiModelId) => `${usd(AI_MODELS[m].price.input)} in, ${usd(AI_MODELS[m].price.output)} out per million tokens`;

export function AdminAiModels() {
  const q = useAiModels();
  const d = q.data;
  usePageTitle("AI models");

  return (
    <>
      <PageHeader
        title="AI models"
        subtitle={
          <>
            Which model does each job Metrickle runs with AI, and why. Switching a model takes effect within a minute, and the reason is published on{" "}
            <a className="text-accent-text underline" href={TRUST_PAGE} target="_blank" rel="noreferrer">
              metrickle.com/trust/ai
            </a>
            .
          </>
        }
      />
      <div className="space-y-4" aria-busy={q.isPending}>
        {q.isError ? (
          <Card>
            <ErrorState error={q.error} />
          </Card>
        ) : !d ? (
          AI_JOB_IDS.map((j) => (
            <Card key={j}>
              <CardSection>
                <Skeleton className="h-16" />
              </CardSection>
            </Card>
          ))
        ) : (
          <>
            {!d.ai && (
              <Callout tone="warn" title="No Anthropic API key.">
                Set the ANTHROPIC_API_KEY secret on the API worker. Until then none of these jobs runs, whichever model is chosen.
              </Callout>
            )}
            {AI_JOB_IDS.map((j) => (
              <JobCard key={j} job={j} live={d.jobs[j]} />
            ))}
            <History d={d} />
          </>
        )}
      </div>
    </>
  );
}

function JobCard({ job, live }: { job: AiJobId; live: AiModelsAdmin["jobs"][AiJobId] }) {
  const [open, setOpen] = useState(false);
  const j = AI_JOBS[job];
  const decision = j.decisions.at(-1)!;
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {j.name}
            <Badge>{j.audience === "customer" ? "Uses customers' AI credits" : "Metrickle team"}</Badge>
            {live.switched ? <Badge tone="warn">Switched</Badge> : <Badge tone="good">Default</Badge>}
          </span>
        }
        subtitle={j.does}
        actions={
          j.models.length > 1 && (
            <Button size="sm" onClick={() => setOpen(true)}>
              Change model
            </Button>
          )
        }
      />
      <CardSection>
        <p className="text-sm text-ink">
          Runs on <strong>{AI_MODELS[live.model].name}</strong>
          <span className="text-ink-3"> · {price(live.model)}</span>
        </p>
        {live.switched ? (
          <p className="mt-1 text-sm text-ink-2">
            Switched from {AI_MODELS[defaultModel(job)].name} <Ago t={live.switched.since} />: {live.switched.reason}
          </p>
        ) : (
          <p className="mt-1 text-sm text-ink-2">
            Chosen {decision.date}: {decision.why}
          </p>
        )}
      </CardSection>
      <SwitchModal job={job} current={live.model} open={open} onOpenChange={setOpen} />
    </Card>
  );
}

function SwitchModal({ job, current, open, onOpenChange }: { job: AiJobId; current: AiModelId; open: boolean; onOpenChange: (o: boolean) => void }) {
  const j = AI_JOBS[job];
  const fallback = defaultModel(job);
  const [model, setModel] = useState<AiModelId>(current);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const m = useApiMutation((v: { model: AiModelId; reason: string }) => switchAiModel(job, v.model === fallback ? null : v.model, v.reason), [AI_MODELS_KEY]);

  useEffect(() => {
    if (!open) return;
    setModel(current);
    setReason("");
    setError(null);
  }, [open, current]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (model === current) return setError("Choose a different model.");
    if (reason.trim().length < 10) {
      setError("Say why in a sentence. It's published.");
      document.getElementById(`ai-reason-${job}`)?.focus();
      return;
    }
    m.mutate(
      { model, reason: reason.trim() },
      {
        onSuccess: () => {
          onOpenChange(false);
          toaster.success({ title: `${j.name} now runs on ${AI_MODELS[model].name}` });
        },
        onError: notifyError,
      },
    );
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={`Change model: ${j.name.toLowerCase()}`} description="Takes effect within a minute. The model and your reason appear on metrickle.com/trust/ai.">
      <form onSubmit={submit} className="space-y-3">
        <RadioCards
          label="Model"
          value={model}
          onValueChange={(v) => {
            setModel(v as AiModelId);
            setError(null);
          }}
          items={j.models.map((id) => ({ value: id, label: `${AI_MODELS[id].name}${id === fallback ? " (default)" : ""}`, hint: price(id) }))}
        />
        <Field label="Why" id={`ai-reason-${job}`} required hint="Published on metrickle.com/trust/ai while this model is in use. Plain words, for customers." error={error ?? undefined}>
          <Textarea
            value={reason}
            maxLength={500}
            onChange={(e) => {
              setReason(e.target.value);
              setError(null);
            }}
            placeholder={model === fallback && current !== fallback ? "Back on the default: the outage is over." : "Anthropic's API returned errors for this model, so we've moved to another while it's fixed."}
          />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" pending={m.isPending} pendingLabel="Switching…">
            {model === fallback && current !== fallback ? "Go back to the default" : "Switch model"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function History({ d }: { d: AiModelsAdmin }) {
  return (
    <Card>
      <CardHeader title="Changes" subtitle="Every switch made here, newest first. Decisions made in code are listed on the public page." />
      <TableScroll label="Model changes">
        <Table caption="Model changes">
          <thead>
            <tr>
              <th scope="col">Job</th>
              <th scope="col">Now on</th>
              <th scope="col">Why</th>
              <th scope="col">By</th>
              <th scope="col">When</th>
            </tr>
          </thead>
          <tbody>
            {d.history.length === 0 ? (
              <TableEmptyRow columns={5}>No switches yet. Every job is on the model chosen for it in code.</TableEmptyRow>
            ) : (
              d.history.map((h) => (
                <tr key={`${h.job}-${h.changedAt}`}>
                  <td className="whitespace-nowrap">{AI_JOBS[h.job]?.name ?? h.job}</td>
                  <td className="whitespace-nowrap">{h.model ? AI_MODELS[h.model].name : AI_JOBS[h.job] ? `${AI_MODELS[defaultModel(h.job)].name} (default)` : "Default"}</td>
                  <td className="min-w-64 whitespace-normal">{h.reason}</td>
                  <td>{h.changedBy ?? <None />}</td>
                  <td>
                    <Ago t={h.changedAt} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableScroll>
    </Card>
  );
}
